import React from 'react';
import bwipjs from 'bwip-js/browser';
import { formatNumber } from '../../shared/numberFormat';

// The Package Label laid out for a Zebra printer (issue #305), in the printer's
// own dots at its own resolution, so nothing is left to a driver's page
// setup. One layout feeds both the ZPL sent to the printer (toZpl) and the
// preview on screen (BoxLabelPreview), so the preview shows the label as it
// will print.
//
// What's on it (vis_order_fulfilment.md "Labels", issue #445), the same
// for every container, printed as it's sealed: "To:" the Customer with the
// box's own number ("Package 1") at the right, a QR code with clear
// space around it and the order number under it, each SKU and
// quantity of its own goods, the Packages directly inside it, and "From:"
// the sender's website in the footer.
// Nothing on it changes as packing goes on, so there's no "of N" (the
// carrier's label carries the count). No prices or addresses.
// Contents that don't fit shrink to the size's smallest readable text;
// past that the label says how many SKUs the package holds and leaves the
// full list to the code (`overflow`).
//
// Text prints in the printer's own scalable font (ZPL font 0). It's
// measured here as bold Arial, which runs a little wider, so whatever fits
// here fits on the label. The QR code goes as a bitmap of whole
// dots per module, drawn from the same symbol the preview shows.

const MM_PER_INCH = 25.4;
const PT_PER_INCH = 72;

const clamp = (n, lo, hi) => Math.min(hi, Math.max(lo, n));

let measureCtx;
export function textWidth(text, h) {
  if (measureCtx === undefined) {
    try {
      measureCtx = document.createElement('canvas').getContext('2d');
    } catch {
      measureCtx = null;
    }
  }
  if (!measureCtx) return String(text).length * h * 0.62;
  measureCtx.font = `bold ${h}px Arial, Helvetica, sans-serif`;
  return measureCtx.measureText(String(text)).width;
}

// The text, cut short with "..." if it's wider than `maxW` dots.
export function fitText(text, h, maxW) {
  const full = String(text || '');
  if (textWidth(full, h) <= maxW) return full;
  for (let n = full.length - 1; n > 0; n -= 1) {
    const cut = `${full.slice(0, n).trimEnd()}...`;
    if (textWidth(cut, h) <= maxW) return cut;
  }
  return '';
}

// The QR symbol: pixx × pixy modules, pixs row by row from the top (1 =
// dark). QR rather than Optim's usual Data Matrix (std-technology-stack.md,
// Barcode Standard): the label's code is a web link for any phone camera,
// and an iPhone's camera opens only QR codes. Error correction M survives
// a scuffed box.
function matrixFor(value) {
  const [symbol] = bwipjs.raw('qrcode', value, 'eclevel=M');
  return { cols: symbol.pixx, rows: symbol.pixy, cells: symbol.pixs };
}

// "5 x PCK": five of the unit, so it can't be read as one five-pack. A
// plain x, which every printer font has.
const qtyText = (c) => `${formatNumber(c.qty, { maxDecimals: 2 })}${c.uom ? ` x ${c.uom}` : ''}`;

// "Wt 2.45 kg", or "Wt ?" when an item in the package has no weight recorded.
// Nothing when there's no weight unit set.
export function weightText(weight) {
  if (!weight) return '';
  if (weight.value === null || weight.value === undefined) return 'Wt ?';
  return `Wt ${formatNumber(weight.value, { maxDecimals: weight.unit === 'kg' || weight.unit === 'lb' ? 2 : 0 })} ${weight.unit}`;
}

// label: { party_name, display_number, box_number, from, contents: [{ sku, qty, uom }], inside: ['Package 2'] }
// size:  { width_mm, height_mm }
export function layoutBoxLabel({ label, link, size, dpi }) {
  const perMm = dpi / MM_PER_INCH;
  const mm = (v) => Math.round(v * perMm);
  const pt = (v) => Math.round((v / PT_PER_INCH) * dpi);
  const widthMm = Number(size.width_mm);
  const heightMm = Number(size.height_mm);
  const W = mm(widthMm);
  const H = mm(heightMm);
  const short = Math.min(widthMm, heightMm);
  // Text sizes and spacing follow the label's short side: 8 pt text on a
  // 69 × 48 mm label, down to a readable 5.5 pt on the smallest.
  const basePt = clamp(short * 0.17, 5.5, 13);
  const minPt = clamp(short * 0.12, 4.5, 7);
  const pad = mm(Math.max(1.5, short * 0.05));
  const clear = mm(Math.max(1.5, short * 0.04));
  const gap = mm(1.5);
  // A label taller than it is wide (A6) puts the code under the header and
  // the contents under that; otherwise the contents sit beside the code.
  const tall = heightMm > widthMm * 1.1;
  const innerW = W - 2 * pad;
  const lineOf = (h) => Math.round(h * 1.2);
  const items = [];
  const text = (x, y, h, value, w, align = 'left') => {
    if (value) items.push({ type: 'text', x, y, h, w: Math.max(1, Math.round(w)), text: value, align });
  };

  // Header, one line: "To:" the Customer at the left, the box's own
  // number at the right, same size. The box number takes at most a little
  // over half the width; a long Customer name is cut short.
  const nameH = pt(basePt * 1.2);
  const numberH = nameH;
  const rightW = Math.min(Math.ceil(innerW * 0.6), Math.ceil(textWidth(label.box_number, nameH)));
  const leftW = innerW - rightW - gap;
  const headerH = lineOf(nameH);
  if (label.party_name) text(pad, pad, nameH, fitText(`To: ${label.party_name}`, nameH, leftW), leftW);
  text(W - pad - rightW, pad, nameH, fitText(label.box_number, nameH, rightW), rightW, 'right');

  // Footer: "From:" the sender's website (or name), across the bottom.
  // The SKUs' own size, so it reads as easily on the smallest label.
  const footH = pt(basePt);
  const footY = H - pad - lineOf(footH);
  if (label.from) text(pad, footY, footH, fitText(`From: ${label.from}`, footH, innerW), innerW);
  const bottom = label.from ? footY - mm(0.5) : H - pad;

  const bodyY = pad + headerH + mm(tall ? 3 : 1);
  const bodyH = bottom - bodyY;

  // The code, a whole number of dots per module, with QR's quiet zone (4
  // modules, and never less than the size's clear space) all round, and
  // the order number under it: scanning the code opens that order.
  const matrix = matrixFor(link);
  const numberRoom = lineOf(numberH);
  const codeRoom = tall ? Math.min(innerW * 0.35, bodyH * 0.35) : Math.min(bodyH - numberRoom, innerW * 0.3 + 2 * clear);
  const moduleDots = Math.max(2, Math.floor(Math.min(codeRoom / (matrix.cols + 8), (codeRoom - 2 * clear) / matrix.cols)));
  const codeDots = moduleDots * matrix.cols;
  const quiet = Math.max(clear, 4 * moduleDots);
  const codeX = pad + quiet;
  const codeY = bodyY + quiet;
  items.push({ type: 'matrix', x: codeX, y: codeY, module: moduleDots, matrix });
  const numberW = Math.ceil(textWidth(label.display_number, numberH));
  const columnW = tall ? innerW : Math.min(Math.max(codeDots + 2 * quiet, numberW + quiet), Math.round(innerW * 0.45));
  const numberY = codeY + codeDots + quiet;
  // Left edge in line with the code's, so the two read as one block.
  text(codeX, numberY, numberH, fitText(label.display_number, numberH, columnW - quiet), columnW - quiet);

  const area = tall
    ? { x: pad, y: numberY + numberRoom + clear, w: innerW }
    : { x: pad + columnW + mm(1), y: bodyY, w: W - pad - (pad + columnW + mm(1)) };
  area.h = bottom - area.y;

  // The body at the largest text size, from the size's own down to its
  // readable minimum, that lists every line: each SKU and quantity, then
  // each Package inside by name.
  const contents = label.contents || [];
  const rows = [
    ...contents.map((c) => ({ left: c.sku || '?', right: qtyText(c) })),
    ...(label.inside || []).map((name) => ({ left: `Inside: ${name}`, right: '' })),
  ];
  let fitted = null;
  for (let p = basePt; p >= minPt - 0.01; p -= 0.5) {
    const h = pt(p);
    const rowH = Math.round(h * 1.3);
    const qtyW = Math.ceil(Math.max(0, ...rows.map((r) => textWidth(r.right, h))));
    if (rows.length * rowH <= area.h && qtyW <= area.w * 0.5) {
      fitted = { h, rowH, qtyW };
      break;
    }
  }

  if (fitted) {
    const { h, rowH, qtyW } = fitted;
    const skuW = area.w - qtyW - gap;
    rows.forEach((r, i) => {
      const y = area.y + i * rowH;
      text(area.x, y, h, fitText(r.left, h, r.right ? skuW : area.w), r.right ? skuW : area.w);
      if (r.right) text(area.x + area.w - qtyW, y, h, r.right, qtyW, 'right');
    });

  } else {
    const skuCount = new Set(contents.map((c) => c.sku)).size;
    const h = 2 * lineOf(pt(basePt)) <= area.h ? pt(basePt) : pt(minPt);
    text(area.x, area.y, h, fitText(`${skuCount} SKU${skuCount === 1 ? '' : 's'}`, h, area.w), area.w);
    text(area.x, area.y + lineOf(h), h, fitText('scan for contents', h, area.w), area.w);
  }

  return { dpi, width: W, height: H, items, overflow: !fitted };
}

// ^FH_ lets field data carry ^ ~ and _ as hex escapes.
const escapeField = (value) => String(value).replace(/_/g, '_5F').replace(/\^/g, '_5E').replace(/~/g, '_7E');

// The code as a ZPL graphic field: each module `module` dots square.
function matrixZpl({ module, matrix }) {
  const widthDots = matrix.cols * module;
  const heightDots = matrix.rows * module;
  const rowBytes = Math.ceil(widthDots / 8);
  let hex = '';
  for (let r = 0; r < matrix.rows; r += 1) {
    const bytes = new Array(rowBytes).fill(0);
    for (let c = 0; c < matrix.cols; c += 1) {
      if (!matrix.cells[r * matrix.cols + c]) continue;
      for (let k = 0; k < module; k += 1) {
        const x = c * module + k;
        bytes[x >> 3] |= 0x80 >> (x & 7);
      }
    }
    const row = bytes.map((b) => b.toString(16).padStart(2, '0')).join('').toUpperCase();
    hex += row.repeat(module);
  }
  const total = rowBytes * heightDots;
  return `^GFA,${total},${total},${rowBytes},${hex}`;
}

// The label in ZPL: page width and length set from the label itself, text
// in font 0 (left-aligned text clipped to its box, right-aligned text in a
// one-line field block), UTF-8.
export function toZpl(layout) {
  const out = ['^XA', '^CI28', `^PW${layout.width}`, `^LL${layout.height}`, '^LH0,0'];
  layout.items.forEach((item) => {
    if (item.type === 'matrix') {
      out.push(`^FO${item.x},${item.y}${matrixZpl(item)}^FS`);
    } else if (item.align === 'right') {
      out.push(`^FO${item.x},${item.y}^A0N,${item.h},${item.h}^FB${item.w},1,0,R,0^FH_^FD${escapeField(item.text)}^FS`);
    } else {
      out.push(`^FO${item.x},${item.y}^A0N,${item.h},${item.h}^TBN,${item.w},${Math.round(item.h * 1.3)}^FH_^FD${escapeField(item.text)}^FS`);
    }
  });
  out.push('^PQ1', '^XZ');
  return out.join('\n');
}

// The layout drawn on screen, `scale` times its real size.
export function BoxLabelPreview({ layout, scale = 1 }) {
  const perMm = layout.dpi / MM_PER_INCH;
  let codePath = '';
  layout.items.forEach((item) => {
    if (item.type !== 'matrix') return;
    const { matrix, module } = item;
    // One rectangle per run of dark cells: a logo can be thousands of them.
    for (let r = 0; r < matrix.rows; r += 1) {
      for (let c = 0; c < matrix.cols; c += 1) {
        if (!matrix.cells[r * matrix.cols + c]) continue;
        const start = c;
        while (c + 1 < matrix.cols && matrix.cells[r * matrix.cols + c + 1]) c += 1;
        const run = (c - start + 1) * module;
        codePath += `M${item.x + start * module},${item.y + r * module}h${run}v${module}h-${run}z`;
      }
    }
  });
  return (
    <svg
      viewBox={`0 0 ${layout.width} ${layout.height}`}
      style={{ width: `${(layout.width / perMm) * scale}mm`, height: `${(layout.height / perMm) * scale}mm`, display: 'block', background: '#fff' }}
      shapeRendering="crispEdges"
    >
      <path d={codePath} fill="#000" />
      {layout.items.filter((item) => item.type === 'text').map((item, i) => (
        <text
          key={i}
          x={item.align === 'right' ? item.x + item.w : item.x}
          y={item.y + item.h * 0.8}
          fontSize={item.h}
          fontWeight={700}
          fontFamily="Arial, Helvetica, sans-serif"
          textAnchor={item.align === 'right' ? 'end' : 'start'}
          fill="#000"
        >
          {item.text}
        </text>
      ))}
    </svg>
  );
}
